import React, { useSyncExternalStore } from 'react';
import { Button } from 'antd';
import AppTheme from '../components/theme/AppTheme';
import { registerNotificationDismisser } from './notificationRegistry';
import { createActionableNoticeStore } from './actionableNoticeStore';
import './actionableNotifications.css';

const store = createActionableNoticeStore(), wrappers = new WeakMap();
export const dismissActionableNotifications = () => store.dismissAll();
// Logout clears UI without acknowledging another user's mailbox work.
registerNotificationDismisser(() => store.dismissAll(false));

function NotificationActions({ id, placement, children }) {
  const latest = useSyncExternalStore(store.subscribe, () => store.latest(placement));
  const rightAligned = placement === 'topRight' || placement === 'bottomRight';
  const dismiss = latest === id && <Button size="small" onClick={event => { event.stopPropagation(); store.dismissAll(); }}>Dismiss all</Button>;
  return <AppTheme><div role="group" aria-label="Notification actions" className={`srm-notification-action-row${rightAligned ? ' srm-notification-action-row-right' : ''}`}>
    {!rightAligned && dismiss}
    <div className="srm-notification-primary-actions">{children}</div>
    {rightAligned && dismiss}
  </div></AppTheme>;
}

// Preserve the original API/keys/timers and callbacks. Do not reopen older
// notices just to move the button: their footer subscribes to the live stack.
export function withNotificationActions(api) {
  if (wrappers.has(api)) return wrappers.get(api);
  const current = new Map();
  const keys = { sequence: 0 };
  const result = { ...api };
  for (const method of ['open', 'info', 'success', 'warning', 'error']) {
    if (!api[method]) continue;
    result[method] = config => {
      const actions = config.actions ?? config.btn;
      if (!actions) { current.get(config.key)?.remove(false); return api[method](config); } // Button-free snackbar: untouched.
      const key = config.key ?? `actionable-notice-${++keys.sequence}`;
      current.get(key)?.remove(false);
      const id = Symbol(), placement = config.placement || 'topRight';
      const entry = {
        remove(notify) {
          if (current.get(key) !== entry) return;
          current.delete(key); store.remove(id);
          if (notify) config.onClose?.();
        },
        dismiss(notify = true) { try { entry.remove(notify); } finally { api.destroy(key); } },
      };
      current.set(key, entry); store.add(id, { placement, dismiss: entry.dismiss });
      const wrapped = { ...config, key, className: [config.className, 'srm-actionable-notification'].filter(Boolean).join(' '), onClose: () => entry.remove(true) };
      delete wrapped.btn; // Accept legacy callers without passing Ant's deprecated prop.
      wrapped.actions = <NotificationActions id={id} placement={placement}>{actions}</NotificationActions>;
      return api[method](wrapped);
    };
  }
  result.destroy = key => {
    if (key === undefined) [...current.values()].forEach(entry => entry.remove(false));
    else current.get(key)?.remove(false);
    return api.destroy(key);
  };
  wrappers.set(api, result); wrappers.set(result, result);
  return result;
}
